"use client";

import { useState } from "react";
import { FormInput, FormTextarea, FormSelect } from "@/components/ui/FormInput";
import { PiCaretDoubleRightBold } from "@/components/icons";
import { kodeMonoBold } from "@/lib/kodeMonoBold";

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  source: string;
  message: string;
}

const sourceOptions = [
  { value: "google", label: "Google Search" },
  { value: "social", label: "Social Media" },
  { value: "friend", label: "Friend/Referral" },
  { value: "blog", label: "Blog/Article" },
  { value: "other", label: "Other" },
];

export default function ContactForm(): React.ReactElement {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    source: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name?: string; value: string } }
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      setSubmitStatus({
        type: "success",
        message: "Message sent successfully! We'll get back to you soon.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        source: "",
        message: "",
      });
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to send message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-10 2xl:mt-12">
      {/* Row 1: Name & Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[15px]">
        <FormInput
          type="text"
          name="name"
          placeholder="Your name"
          required
          value={formData.name}
          onChange={handleInputChange}
        />
        <FormInput
          type="email"
          name="email"
          placeholder="email@example.com"
          required
          value={formData.email}
          onChange={handleInputChange}
        />
      </div>

      {/* Row 2: Subject & Source */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[15px] mt-[15px]">
        <FormInput
          type="text"
          name="subject"
          placeholder="Subject"
          required
          value={formData.subject}
          onChange={handleInputChange}
        />
        <FormSelect
          name="source"
          placeholder="Where did you hear about us?"
          required
          options={sourceOptions}
          value={formData.source}
          onChange={handleInputChange}
        />
      </div>

      {/* Row 3: Message */}
      <div className="mt-[15px]">
        <FormTextarea
          name="message"
          placeholder="Your message"
          required
          rows={5}
          value={formData.message}
          onChange={handleInputChange}
        />
      </div>

      {/* Status Message */}
      {submitStatus.type && (
        <div
          className={`mt-[15px] px-[25px] py-[16px] border text-[11px] font-kode-mono ${submitStatus.type === "success"
            ? "border-accent-green/5 text-accent-green/80 bg-accent-green/7"
            : "border-accent-red/5 text-accent-red/80 bg-accent-red/7"
            }`}
        >
          {submitStatus.message}
        </div>
      )}

      {/* Footer: Privacy text & Submit button */}
      <div className="flex justify-between items-center mt-[18px]">
        <span className="text-foreground/50 text-[11px] font-kode-mono">
          Zero spam. Zero sharing.
        </span>
        <button
          type="submit"
          disabled={isSubmitting}
          className={`${kodeMonoBold.variable} relative flex items-center gap-[6px] text-accent-gold text-[14px] font-kode-mono-bold uppercase tracking-[0.42px] bg-transparent cursor-pointer hover:opacity-80 transition-opacity group disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isSubmitting ? "Sending..." : "Dispatch Message"}
          {!isSubmitting && <PiCaretDoubleRightBold className="text-[14px]" />}
          <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-accent-gold transition-all duration-300 ease-in-out group-hover:w-full"></span>
        </button>
      </div>
    </form>
  );
}
